import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Key, Smartphone, Fingerprint, Plus, Trash2, History, CheckCircle, AlertTriangle } from 'lucide-react';
import { useAuthStore } from '@/store/authStore';
import { authenticationService } from '@/services/authenticationService';
import { TwoFactorSetup } from '@/components/auth/TwoFactorSetup';
import { Breadcrumb } from '@/components/ui/Breadcrumb';
import { useToast } from '@/hooks/useToast';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';

export function SecurityPage() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { success, error } = useToast();
  const [showTwoFactorSetup, setShowTwoFactorSetup] = useState(false);
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [biometricEnabled, setBiometricEnabled] = useState(false);
  const [securityMethods, setSecurityMethods] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    window.scrollTo(0, 0);
    loadSecuritySettings();
  }, []);

  const loadSecuritySettings = async () => {
    setLoading(true);
    try {
      // Simulation of security state - in real app would come from API
      setTwoFactorEnabled(true);
      setBiometricEnabled(false);
      setSecurityMethods([
        {
          id: '1',
          type: 'AUTHENTICATOR',
          name: 'Authenticator App',
          enabled: true,
          lastUsed: new Date(Date.now() - 2 * 24 * 60 * 60 * 1000)
        },
        {
          id: '2',
          type: 'SMS',
          name: 'SMS (+353 *** ***45)',
          enabled: false,
          lastUsed: null
        }
      ]);
    } catch (err) {
      error('Failed to load security settings.');
    } finally {
      setLoading(false);
    }
  };

  const handleDisableTwoFactor = async (methodId: string) => {
    if (!user?.id) return;
    try {
      await authenticationService.disableTwoFactor(user.id, methodId);
      success('Two-factor method disabled successfully.');
      loadSecuritySettings();
    } catch (err) {
      error('Failed to disable method.');
    }
  };

  const handleEnableBiometric = async () => {
    if (!user?.id) return;
    try {
      const { options } = await authenticationService.setupBiometric(user.id, 'FINGERPRINT');
      // @ts-ignore - Navigator credentials type
      const credential = await navigator.credentials.create({ publicKey: options });

      if (credential) {
        await authenticationService.registerBiometric(user.id, 'FINGERPRINT', {
          id: credential.id,
          // @ts-ignore
          publicKey: 'mock_public_key' // In real app would be actual public key from credential
        });
        setBiometricEnabled(true);
        success('Biometric login enabled successfully.');
      }
    } catch (err) {
      error('Biometric setup failed or was cancelled.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900 pb-20">
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 focus:z-[100] focus:px-4 focus:py-2 focus:bg-primary-600 focus:text-white focus:rounded-lg focus:shadow-lg min-h-touch">
        Skip to main content
      </a>
      <main id="main-content" role="main" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <Breadcrumb items={[{ label: 'Dashboard', href: '/dashboard' }, { label: 'Security Settings' }]} />

        <div className="mb-8 mt-4">
          <h1 className="text-3xl font-bold text-gray-900">Security Settings</h1>
          <p className="text-gray-600 mt-2">Manage your account security and authentication preferences.</p>
        </div>

        <div className="space-y-6">
          {/* 2FA Section */}
          <Card className="p-6">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-50 text-blue-600 rounded-lg">
                  <Shield className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Two-Factor Authentication</h3>
                  <p className="text-sm text-gray-500">Add an extra layer of security to your account.</p>
                </div>
              </div>
              <Badge variant={twoFactorEnabled ? 'success' : 'warning'}>
                {twoFactorEnabled ? 'Enabled' : 'Disabled'}
              </Badge>
            </div>

            {twoFactorEnabled ? (
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-blue-800">
                    Your account is protected with two-factor authentication. New sign-ins will require a verification code.
                  </p>
                </div>

                <div className="space-y-2">
                  <h4 className="text-sm font-medium text-gray-700">Authentication Methods</h4>
                  {securityMethods.map((method) => (
                    <div key={method.id} className="flex items-center justify-between p-4 bg-white border border-gray-200 rounded-lg">
                      <div className="flex items-center gap-4">
                        <div className="p-2 bg-gray-100 rounded-md">
                          {method.type === 'AUTHENTICATOR' ? <Smartphone className="w-5 h-5 text-gray-600" /> : <Shield className="w-5 h-5 text-gray-600" />}
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">{method.name}</p>
                          <div className="flex items-center gap-2 text-xs text-gray-500">
                            <History className="w-3 h-3" />
                            {method.lastUsed ? `Last used: ${method.lastUsed.toLocaleDateString()}` : 'Never used'}
                          </div>
                        </div>
                      </div>
                      {method.enabled && (
                        <Button size="sm" variant="ghost" className="text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => handleDisableTwoFactor(method.id)} icon={<Trash2 className="w-4 h-4" />}>Remove</Button>
                      )}
                    </div>
                  ))}
                </div>

                <Button variant="outline" onClick={() => setShowTwoFactorSetup(true)} icon={<Plus className="w-4 h-4" />}>Add Method</Button>
              </div>
            ) : (
              <div className="text-center py-8 bg-gray-50 rounded-lg border border-gray-200 border-dashed">
                <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-3" />
                <h4 className="text-lg font-medium text-gray-900 mb-2">Secure your account</h4>
                <p className="text-gray-500 mb-6 max-w-sm mx-auto">
                  Protect your account from unauthorized access by enabling two-factor authentication.
                </p>
                <Button onClick={() => setShowTwoFactorSetup(true)}>Set Up 2FA</Button>
              </div>
            )}
          </Card>

          {/* Biometric Section */}
          <Card className="p-6">
            <div className="flex items-start justify-between mb-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-purple-50 text-purple-600 rounded-lg">
                  <Fingerprint className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Biometric Login</h3>
                  <p className="text-sm text-gray-500">Use your device's fingerprint or face ID to log in.</p>
                </div>
              </div>
              <Badge variant={biometricEnabled ? 'success' : 'secondary'}>
                {biometricEnabled ? 'Enabled' : 'Disabled'}
              </Badge>
            </div>

            {biometricEnabled ? (
              <div className="bg-green-50 border border-green-100 rounded-lg p-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-5 h-5 text-green-600" />
                  <span className="text-sm text-green-800">Biometric login is active on this device.</span>
                </div>
                <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50">Disable</Button>
              </div>
            ) : (
              <div className="flex items-center justify-between bg-gray-50 p-4 rounded-lg border border-gray-200">
                <p className="text-sm text-gray-600">Faster login with Touch ID or Face ID.</p>
                <Button variant="outline" onClick={handleEnableBiometric}>Enable Biometrics</Button>
              </div>
            )}
          </Card>

          {/* Security Tips */}
          <Card className="p-6 bg-gray-50 border-none shadow-none">
            <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Shield className="w-4 h-4" /> Security Recommendations
            </h4>
            <ul className="space-y-3 text-sm text-gray-600 list-disc list-inside">
              <li>Use a strong, unique password for your account.</li>
              <li>Enable two-factor authentication for better protection.</li>
              <li>Review your active sessions periodically.</li>
              <li>Never share your password or verification codes.</li>
            </ul>
          </Card>
        </div>
      </main>

      {showTwoFactorSetup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
              <h3 className="text-lg font-bold text-gray-900">Set Up Two-Factor Authentication</h3>
              <button onClick={() => setShowTwoFactorSetup(false)} className="text-gray-400 hover:text-gray-600">×</button>
            </div>
            <div className="p-6">
              <TwoFactorSetup onComplete={() => {
                setShowTwoFactorSetup(false);
                loadSecuritySettings();
              }} />
            </div>
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-end">
              <Button variant="outline" onClick={() => setShowTwoFactorSetup(false)}>Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
